import { useCallback, useMemo } from "react"

import {
  useTransportMutation,
  useTransportPaginatedQuery,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { ListScheduledRunsRequest } from "@/types_api/backend/transport/list_scheduled_runs_request"
import type { UpdateScheduledRunTransportRequest } from "@/types_api/backend/transport/update_scheduled_run_transport_request"

const SCHEDULED_RUNS_LIST_REQUEST = {
  page_size: 100,
} satisfies ListScheduledRunsRequest

interface ScheduledRunsListTransportOptions {
  enabled?: boolean
  templateId?: string
}

export function useScheduledRunsListTransport(
  options?: ScheduledRunsListTransportOptions,
) {
  const request = useMemo(
    () => ({
      ...SCHEDULED_RUNS_LIST_REQUEST,
      ...(options?.templateId ? { template_id: options.templateId } : {}),
    }),
    [options?.templateId],
  )

  return useTransportPaginatedQuery({
    combinePages: (pages) => ({
      next_page_token: pages.at(-1)?.next_page_token ?? null,
      scheduled_runs: pages.flatMap((page) => page.scheduled_runs),
      total_count: pages.at(-1)?.total_count ?? 0,
    }),
    getNextRequest: (page, previousRequest) =>
      page.next_page_token
        ? {
            ...(previousRequest ?? request),
            page_token: page.next_page_token,
          }
        : null,
    operation: "listScheduledRuns",
    request,
    options: { enabled: options?.enabled },
  })
}

export function useScheduledRunTransport(
  scheduledRunId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useTransportQuery({
    operation: "getScheduledRun",
    request: { scheduled_run_id: scheduledRunId ?? "" },
    options: {
      ...options,
      enabled: Boolean(scheduledRunId) && (options?.enabled ?? true),
    },
  })
}

export function useScheduledRunsTransportInvalidation() {
  const transportQueryUtils = useTransportQueryUtils()

  const invalidateList = useCallback(
    () => transportQueryUtils.invalidatePaginatedOperation("listScheduledRuns"),
    [transportQueryUtils],
  )

  const invalidateDetail = useCallback(
    (scheduledRunId: string) =>
      transportQueryUtils.invalidate("getScheduledRun", {
        scheduled_run_id: scheduledRunId,
      }),
    [transportQueryUtils],
  )

  const invalidateDetailAndList = useCallback(
    (scheduledRunId: string) =>
      Promise.all([invalidateDetail(scheduledRunId), invalidateList()]),
    [invalidateDetail, invalidateList],
  )

  return {
    invalidateDetail,
    invalidateDetailAndList,
    invalidateList,
  }
}

export function useCreateScheduledRunTransport(options?: {
  onSuccess?: () => void
}) {
  return useTransportMutation("createScheduledRun", options)
}

export function useUpdateScheduledRunTransport(options?: {
  onError?: (error: unknown) => void
  onSuccess?: () => void
}) {
  const mutation = useTransportMutation("updateScheduledRun", options)

  const updateScheduledRun = useCallback(
    async ({
      scheduled_run_id,
      ...request
    }: UpdateScheduledRunTransportRequest & { scheduled_run_id: string }) =>
      mutation.mutateAsync({ ...request, scheduled_run_id }),
    [mutation],
  )

  return {
    ...mutation,
    updateScheduledRun,
  }
}

export function useDeleteScheduledRunTransport(options?: {
  onSuccess?: () => void
}) {
  return useTransportMutation("deleteScheduledRun", options)
}

export function useSetScheduledRunEnabledTransport(options?: {
  onSuccess?: () => void
}) {
  const mutation = useTransportMutation("updateScheduledRun", options)

  const setEnabled = useCallback(
    async ({ enabled, id }: { enabled: boolean; id: string }) =>
      mutation.mutateAsync({
        enabled,
        scheduled_run_id: id,
      }),
    [mutation],
  )

  return {
    ...mutation,
    setEnabled,
  }
}
